"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { AddStockDialog } from "../watchlist/_components/add-stock-dialog";
import { ActionBadge } from "@/components/shared/action-badge";
import type {
  ActiveStockVM,
  HomeFilterMode,
} from "./types";

interface ActiveStocksGridProps {
  stocks: ActiveStockVM[];
}

function ConvictionDelta({ delta }: { delta: number | null }) {
  if (delta === null || delta === undefined) return null;
  if (delta === 0) {
    return (
      <span className="text-[11px] font-medium text-muted-foreground" aria-label="No change in conviction">
        ±0
      </span>
    );
  }
  const positive = delta > 0;
  const label = `${positive ? "+" : ""}${delta} this week`;
  return (
    <span
      className={`text-[11px] font-semibold ${positive ? "text-gold" : "text-red-400"}`}
      aria-label={label}
      title={label}
    >
      {positive ? "+" : ""}
      {delta}
    </span>
  );
}

function relativeDays(iso: string | null): string {
  if (!iso) return "no brief yet";
  const date = new Date(iso);
  const ms = Date.now() - date.getTime();
  const days = Math.floor(ms / (24 * 3600_000));
  if (days <= 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

export function ActiveStocksGrid({ stocks }: ActiveStocksGridProps) {
  const router = useRouter();
  const [mode, setMode] = useState<HomeFilterMode>("conviction");
  const [addOpen, setAddOpen] = useState(false);

  const filtered = useMemo(() => {
    const base = [...stocks];
    if (mode === "changed") {
      base.sort((a, b) => Math.abs(b.convictionDelta ?? 0) - Math.abs(a.convictionDelta ?? 0));
      return base.filter(
        (s) => s.convictionDelta !== null && s.convictionDelta !== 0,
      );
    }
    if (mode === "unchanged") {
      return base.filter(
        (s) => s.convictionDelta === null || s.convictionDelta === 0,
      );
    }
    // conviction (descending; nulls last)
    base.sort((a, b) => {
      if (a.conviction === null && b.conviction === null) return 0;
      if (a.conviction === null) return 1;
      if (b.conviction === null) return -1;
      return b.conviction - a.conviction;
    });
    return base;
  }, [stocks, mode]);

  const filterButtonClass = (active: boolean) =>
    `text-[12px] font-semibold px-3 py-1.5 rounded-md transition cursor-pointer border-none ${
      active
        ? "bg-blue/15 text-blue"
        : "bg-transparent text-muted-foreground hover:text-foreground hover:bg-surface-light/20"
    }`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className="inline-flex items-center gap-1 rounded-lg border border-surface-light/30 bg-surface/40 p-1"
          role="group"
          aria-label="Filter active stocks"
        >
          <button
            type="button"
            onClick={() => setMode("changed")}
            aria-pressed={mode === "changed"}
            className={filterButtonClass(mode === "changed")}
          >
            Changed this week
          </button>
          <button
            type="button"
            onClick={() => setMode("unchanged")}
            aria-pressed={mode === "unchanged"}
            className={filterButtonClass(mode === "unchanged")}
          >
            Unchanged
          </button>
          <button
            type="button"
            onClick={() => setMode("conviction")}
            aria-pressed={mode === "conviction"}
            className={filterButtonClass(mode === "conviction")}
          >
            By conviction
          </button>
        </div>

        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="px-3.5 py-1.5 rounded-md bg-blue text-white text-[13px] font-semibold hover:opacity-90 transition cursor-pointer border-none"
        >
          + Add stock to Compass
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-surface-light/30 bg-surface/40 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            No stocks match this filter.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <Link
              key={`${s.exchange}-${s.ticker}`}
              href={`/compass/${s.ticker.toLowerCase()}`}
              aria-label={`${s.companyName} (${s.ticker} ${s.exchange}), open brief`}
              className="group block rounded-xl border border-surface-light/30 bg-surface/40 p-4 hover:border-blue/40 hover:bg-surface/60 transition no-underline"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex flex-col min-w-0">
                  <span className="text-[11px] font-mono text-muted-foreground">
                    {s.ticker} · {s.exchange}
                  </span>
                  <span className="text-sm font-semibold text-foreground truncate">
                    {s.companyName}
                  </span>
                </div>
                <ActionBadge action={s.action} className="text-[10px] font-bold">
                  {s.action ?? "PENDING"}
                </ActionBadge>
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-baseline gap-1">
                  <span className="text-xl font-bold text-foreground">
                    {s.conviction ?? "-"}
                  </span>
                  <span className="text-[11px] text-muted-foreground">/10</span>
                </div>
                <div className="flex-1 h-1.5 rounded-full bg-surface-light/30 overflow-hidden">
                  <div
                    className="h-full bg-blue/70 rounded-full"
                    style={{ width: `${((s.conviction ?? 0) / 10) * 100}%` }}
                  />
                </div>
                <ConvictionDelta delta={s.convictionDelta} />
              </div>

              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span>
                  {s.lastBriefAt ? `Last brief ${relativeDays(s.lastBriefAt)}` : "No brief yet"}
                </span>
                {s.accuracyBadgeText ? (
                  <span
                    className="inline-flex items-center rounded-md bg-blue/10 text-blue px-1.5 py-0.5 font-semibold tracking-wide"
                    title={`Outcome accuracy: ${s.accuracyBadgeText}`}
                    aria-label={`Outcome accuracy: ${s.accuracyBadgeText}`}
                  >
                    {s.accuracyBadgeText}
                  </span>
                ) : null}
              </div>

              {s.headline ? (
                <p className="mt-3 text-xs text-foreground/80 line-clamp-2 leading-relaxed">
                  {s.headline}
                </p>
              ) : null}
            </Link>
          ))}
        </div>
      )}

      <AddStockDialog
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={() => router.refresh()}
      />
    </div>
  );
}
